import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { HttpStatusCode } from "axios";
import clsx from "clsx";

import { getAirLevelAverage } from "../../../libs/iot";
import { isArrayEmpty } from "../../../libs/common";

import {
  AirInfo,
  FloorAirInfo,
  SpaceAirInfo,
  TAG_TYPE,
  getMonitoringAir,
} from "../../../services/iotServices";

import { useUserContext } from "../../../contexts/userContext";

import useSimplePopup from "../../../hooks/useSimplePopup";
import useFullScreenLoading from "../../../hooks/useFullScreenLoading";
import useServiceGroupBuilding from "../../../hooks/useServiceGroupBuilding";

import StickyHeader from "../../../components/Layout/StickyHeader";
import HorizontalRule from "../../../components/Rule/Horizontal";
import PopupSpaceAir from "../../../components/Popup/SpaceAir";
import AirMonitoringSensorFixNotice from "../../../components/Service/AirMonitoring/SensorFixNotice";
import IotWorkingFloor from "../../../components/Service/Iot/WorkingFloorBadge";

import { NoDataIcon } from "../../Center";

type Props = { isManager?: boolean };

export default function ManagerAir({ isManager = true }: Props) {
  const [user] = useUserContext();
  const [isLoading, setIsLoading] = React.useState(false);
  const loadingComponent = useFullScreenLoading({ isLoading });
  const navigate = useNavigate();
  const [airInfo, setAirInfo] = React.useState<AirInfo>();
  const [popup, setPopup] = React.useState("");
  const [selectedFloorInfo, setSelectedFloorInfo] =
    React.useState<FloorAirInfo>();
  const [selectedSpaceInfo, setSelectedSpaceInfo] = useState<SpaceAirInfo>();
  const [selectedBuilding, selectBuildingComponent] = useServiceGroupBuilding({
    categoryCode: "MONITORING",
  });
  const [simplePopupComponent, { open }] = useSimplePopup({});

  const loadAirInfo = async () => {
    setIsLoading(true);
    try {
      const result = await getMonitoringAir({
        buildingId: selectedBuilding?.buildingId ?? undefined,
      });
      if (result.status === HttpStatusCode.Ok) {
        setAirInfo(result.data[0]);
      } else {
        throw new Error("공기질 정보를 불러오는데 실패했습니다.");
      }
    } catch (e: any) {
      if (e?.response?.data?.error) {
        open(e.response.data.error?.replace(". ", ".\n"));
      } else {
        open("공기질 정보를 불러오는데 실패했습니다.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const isShowFloor = (floor: FloorAirInfo) => {
    return !floor.spaces
      .map((space) => getAirLevelAverage(space))
      .every((average) => isNaN(average));
  };

  useEffect(() => {
    if (user.isLoading === false && selectedBuilding?.buildingId) {
      loadAirInfo();
    }
  }, [user.isLoading, selectedBuilding?.buildingId]);

  return (
    <div className="px-5 flex flex-col gap-5">
      {loadingComponent}
      <StickyHeader
        title={isManager ? "공기질 모니터링" : "공기질 확인"}
        onBackClick={() => navigate("/view")}
        rightContent={
          <div onClick={() => loadAirInfo()}>
            <ManagerAirRefreshIcon />
          </div>
        }
      />

      <div>
        <div className="pb-[30px]">{selectBuildingComponent}</div>
        <HorizontalRule />
      </div>

      <div className="pb-2.5 flex flex-col gap-5">
        <div className="flex items-center justify-between gap-2.5">
          <p className="flex-1 py-2 font-medium text-lg">층 별 공기질 오염도</p>
        </div>

        <AirMonitoringSensorFixNotice
          isManager={isManager}
          availableCount={
            airInfo?.floors?.filter((floor) => floor.spaces.length > 0)
              ?.length ?? 0
          }
        />
      </div>

      <div>
        <HorizontalRule
          className="border-black !border-t-2 border-l-0 border-r-0 border-b-0"
          margin="0"
        />
        {!isArrayEmpty(
          airInfo?.floors?.filter(
            (floor) => floor.spaces.length > 0 && isShowFloor(floor)
          )
        ) ? (
          airInfo?.floors
            .filter((floor) => floor.spaces.length > 0 && isShowFloor(floor))
            .map((floor, index, floorList) => (
              <div key={floor.buildingFloorId} className="flex flex-col">
                <div className="w-full flex gap-[15px]">
                  <div className="bg-[#fafafa] w-[130px] flex flex-col items-center justify-center text-lg font-semibold border-r border-[#f3f3f3]">
                    {floor.floorName}
                    {!isManager && (
                      <IotWorkingFloor
                        buildingFloorId={floor.buildingFloorId}
                        userBuildingFloorId={user.buildingFloorId}
                      />
                    )}
                  </div>

                  <div className="flex flex-col w-full">
                    {floor.spaces
                      .filter((item) => !isNaN(getAirLevelAverage(item)))
                      .map((space, index, spaceList) => (
                        <ManagerAirSpaceRow
                          key={space.buildingSpaceId}
                          space={space}
                          spaceList={spaceList}
                          onClick={(popup) => {
                            setSelectedFloorInfo(floor);
                            setSelectedSpaceInfo(space);
                            setPopup(popup);
                          }}
                        />
                      ))}
                  </div>
                </div>
                {index < (floorList?.length ?? 0) - 1 && (
                  <HorizontalRule margin="0" />
                )}
              </div>
            ))
        ) : (
          <div className="sticky top-[72px] w-full h-40 flex flex-col items-center justify-center gap-2">
            <NoDataIcon />
            센서 데이터가 없어요
          </div>
        )}
        <HorizontalRule
          className="border-black !border-t-2 border-l-0 border-r-0 border-b-0"
          margin="0"
        />
      </div>

      <PopupSpaceAir
        popup={popup}
        floorInfo={selectedFloorInfo}
        spaceInfo={selectedSpaceInfo}
        onClose={() => setPopup("")}
      />

      {simplePopupComponent}
    </div>
  );
}

export type ManagerAirSpaceRowProps = {
  space: SpaceAirInfo;
  spaceList: SpaceAirInfo[];
  onClick?: (popup: string) => void;
};

export const ManagerAirSpaceRow = ({
  space,
  spaceList,
  onClick,
}: ManagerAirSpaceRowProps) => {
  const score = space?.tag.find(
    (tag) => tag.tagTypeId === TAG_TYPE.AWAIR_SCORE
  );
  const levelAverage = score?.value ?? getAirLevelAverage(space);
  const index = spaceList.findIndex(
    (item) => item.buildingSpaceId === space.buildingSpaceId
  );

  const levelLabel = (level: number) => {
    return (
      <div className="flex gap-2.5 items-center">
        <span
          className={clsx([
            "text-lg font-semibold",
            getAirLevelTextColor(level),
          ])}
        >
          {level.toFixed(0)}
        </span>
        <div
          className={clsx([
            "px-2.5 py-[5px] rounded-full text-sm font-semibold text-white",
            getAirLevelBgColor(level),
          ])}
        >
          {getAirLevelText(level)}
        </div>
      </div>
    );
  };

  return (
    <>
      <div
        className="py-[15px] flex items-center gap-2.5 justify-between"
        onClick={() => {
          if (!isNaN(levelAverage)) onClick?.("detailFloorAir");
        }}
      >
        <div className="text-sm font-medium">{space.spaceName}</div>
        {isNaN(levelAverage) ? (
          <div className="py-[5px] px-5 rounded-full bg-brand-main-page-background text-brand-neutral-50 text-sm font-medium text-center w-[120px]">
            센서 미설치
          </div>
        ) : (
          <div className="flex items-center gap-2.5 text-sm font-semibold">
            {levelLabel(levelAverage)}
            <ManagerAirRightArrowIcon />
          </div>
        )}
      </div>
      {index < spaceList.length - 1 && (
        <HorizontalRule
          className="border-t border-l-0 border-r-0 border-b-0 border-[#f3f3f3]"
          margin="0"
        />
      )}
    </>
  );
};

export const getAirLevelTextColor = (level: number) => {
  return {
    "text-[#00CDC1]": level >= 80,
    "text-[#42C93A]": level >= 70 && level < 80,
    "text-[#F2AE01]": level >= 60 && level < 70,
    "text-[#FD6900]": level >= 50 && level < 60,
    "text-[#E7004B]": level < 50,
  };
};

export const getAirLevelBgColor = (level: number) => {
  return {
    "bg-[#00CDC1]": level >= 80,
    "bg-[#42C93A]": level >= 70 && level < 80,
    "bg-[#F2AE01]": level >= 60 && level < 70,
    "bg-[#FD6900]": level >= 50 && level < 60,
    "bg-[#E7004B]": level < 50,
  };
};

export const getAirLevelText = (level: number) => {
  let label = "나쁨";
  if (level >= 80) {
    label = "좋음";
  } else if (level >= 70) {
    label = "양호"
  } else if (level >= 60) {
    label = "보통";
  } else if (level >= 50) {
    label = "미흡";
  }
  return label;
};

export const ManagerAirRightArrowIcon = () => (
  <svg
    width="24"
    height="25"
    viewBox="0 0 24 25"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g opacity="0.5">
      <path
        d="M9 18.9954L15 12.9954L9 6.99536"
        stroke="black"
        strokeWidth="1.75"
        strokeLinecap="square"
        strokeLinejoin="round"
      />
    </g>
  </svg>
);

export const ManagerAirRefreshIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M2.85331 8.77173L4.50015 11.6241C4.52542 11.6679 4.58137 11.6829 4.62513 11.6576L7.47754 10.0108"
      stroke="black"
      strokeWidth="1.75"
      strokeLinecap="square"
    />
    <path
      d="M4.68262 11.6742C4.79316 8.94731 6.25602 6.33445 8.79433 4.86895C12.7327 2.59514 17.7686 3.94452 20.0425 7.88288C22.3163 11.8212 20.9669 16.8572 17.0285 19.131C14.3437 20.6811 11.1487 20.5474 8.66652 19.0574C7.50764 18.3618 6.50412 17.3706 5.78041 16.1171"
      stroke="black"
      strokeWidth="1.75"
    />
  </svg>
);
